import { useState, useEffect, useMemo } from "react";
import type { Key } from "react";
import moment from "moment";
import type { Moment } from "moment";
import { DataNode } from "antd/lib/tree";
import type { IOfferHierarchy } from "../../../../store/slices/offering/types";
import type { IOfferParamsTableRecord } from "./types";
import { infinityDateHttpFormat, toMomentObjectDates } from "../../../../lib/utils";
import type { IFormFieldData, Offer } from "../../../../lib/types";
import { IOfferListSelectMode } from "../../../OfferContainer/context/types";
import { OFFER_STATUSES } from "../../../../store/slices/offering/const";
import { parseRuleCondition } from "../../../OfferContainer/utils/parseRuleCondition";

interface IProps {
  initialFromDate: Moment;
  initialQuantity: number;
  agreementId: number;
  offerListSelectMode: IOfferListSelectMode;
  offer?: Offer<string>;
  offerHierarchy?: IOfferHierarchy;
  hierarchyOfferList: Offer<string>[];
  treeDataFormat: DataNode[];
  setTreeDataFormat: React.Dispatch<React.SetStateAction<DataNode[]>>;
}

export const useProductTableData = (props: IProps) => {
  const {
    initialFromDate,
    agreementId,
    offerListSelectMode,
    hierarchyOfferList,
    offer,
    offerHierarchy,
    treeDataFormat,
    initialQuantity,
    setTreeDataFormat,
  } = props;

  // Для генерации строки таблицы при изменении offer
  const getTableRecord = (
    offerItem: Partial<Offer<string>>,
    parentId?: Key
  ): IOfferParamsTableRecord => {
    const key = [parentId, offerItem.id].filter(Boolean).join("-");
    const hierarchyOffer =
      offerListSelectMode === "new"
        ? offer!
        : hierarchyOfferList.find(({ id }) => id === offerItem.id)!;
    const rootHierarchyOfferId = parentId ? String(parentId).split("-")[0] : offerItem.id;
    const rootHierarchyOffer = rootHierarchyOfferId
      ? hierarchyOfferList.find(({ id }) => id === Number(rootHierarchyOfferId))
      : undefined;

    const fromDateSelectMode = moment(rootHierarchyOffer?.fromDate).isAfter(moment(), "day")
      ? moment(rootHierarchyOffer?.fromDate)
      : initialFromDate;

    const serviceRates =
      hierarchyOffer?.serviceRates?.map((rate) => toMomentObjectDates(rate)) || [];
    const hideTsError = hierarchyOffer?.aggregationRules?.forEach((rule) => {
      const hideTsError1 = rule?.serviceRates?.forEach((rate) => {
        if (!serviceRates.map(({ id }) => id).includes(rate.id)) {
          serviceRates.push(toMomentObjectDates(rate));
        }
      });
    });

    const name = offerListSelectMode === "select" ? hierarchyOffer?.name : undefined;

    return {
      id: offerItem.id!,
      parentId,
      key,
      extName: offerItem.extName!,
      name,
      serviceStatusId: undefined,
      quantity: initialQuantity,
      fromDate: offerListSelectMode === "select" ? fromDateSelectMode : initialFromDate,
      toDate: moment(infinityDateHttpFormat),
      offering: {
        ...(hierarchyOffer || {}),
        name,
        agreementId,
        offeringTypeId: offerListSelectMode === "select" ? hierarchyOffer?.offeringTypeId : 3,
        offeringStatusId: OFFER_STATUSES.ACTIVE,
        fromDate:
          offerListSelectMode === "select" ? moment(hierarchyOffer?.fromDate) : initialFromDate,
        toDate:
          offerListSelectMode === "select"
            ? moment(hierarchyOffer?.toDate)
            : moment(infinityDateHttpFormat),
        aggregationRules: hierarchyOffer?.aggregationRules?.map((rule) => ({
          ...rule,
          ruleCondition: parseRuleCondition(rule.ruleCondition),
          fromDate: offerListSelectMode === "select" ? moment(rule.fromDate) : initialFromDate,
          toDate:
            offerListSelectMode === "select" ? moment(rule.toDate) : moment(infinityDateHttpFormat),
        })),
        serviceRates: serviceRates.map((rate) => {
          if (offerListSelectMode === "select") {
            return rate;
          }
          return { ...rate, fromDate: initialFromDate, toDate: moment(infinityDateHttpFormat) };
        }),
        serviceProfile: {
          ...hierarchyOffer?.serviceProfile,
          profileTypeId:
            offerListSelectMode === "select" ? hierarchyOffer?.serviceProfile?.paymentTypeId : 2,
          fromDate:
            offerListSelectMode === "select"
              ? moment(hierarchyOffer?.serviceProfile.fromDate)
              : initialFromDate,
          toDate:
            offerListSelectMode === "select"
              ? moment(hierarchyOffer?.serviceProfile.toDate)
              : moment(infinityDateHttpFormat),
        },
      },
      minCardinality: offerItem?.minCardinality,
      maxCardinality: offerItem?.maxCardinality,
      defaultCardinality: offerItem?.defaultCardinality,
      prolongTypeId: undefined,
    };
  };
  // Для генерации таблицы при изменении offer и hierarchyOfferList
  const getTableData = (): IOfferParamsTableRecord[] => {
    const data: IOfferParamsTableRecord[] = [];
    // if (offerListSelectMode === "new") {
    //   console.log({ newProduct });
    //   console.log({ data });
    //   data.push(newProduct);
    //   console.log({ data });
    //   return data;
    // }
    const addOfferHierarchyData = (item: Partial<IOfferHierarchy>, parentId?: Key) => {
      data.push(getTableRecord(item, parentId));
      if (item?.children && item.children?.length > 0) {
        item.children.forEach((itemChild) => {
          const grandParentId = [parentId, item.id].filter(Boolean).join("-"); // для синхронизации с деревом
          addOfferHierarchyData(itemChild, grandParentId);
        });
      }
    };
    if (offerHierarchy) {
      addOfferHierarchyData(offerHierarchy);
    } else if (offer) {
      data.push(getTableRecord(offer));
    }
    return data;
  };

  // Для генерации строки таблицы при изменении offerHierarchy и hierarchyOfferList
  const getTableRecordForTree = (offerItem: any, parentId?: Key): IOfferParamsTableRecord => {
    const { key } = offerItem;
    const hierarchyOffer =
      offerListSelectMode === "new"
        ? offer!
        : hierarchyOfferList.find(({ id }) => id === offerItem.id)!;

    const rootHierarchyOfferId = parentId ? String(parentId).split("-")[0] : offerItem.id;
    const rootHierarchyOffer = rootHierarchyOfferId
      ? hierarchyOfferList.find(({ id }) => id === Number(rootHierarchyOfferId))
      : undefined;

    const fromDateSelectMode = moment(rootHierarchyOffer?.fromDate).isAfter(moment(), "day")
      ? moment(rootHierarchyOffer?.fromDate)
      : initialFromDate;

    const serviceRates =
      hierarchyOffer?.serviceRates?.map((rate) => toMomentObjectDates(rate)) || [];
    const hideTsError = hierarchyOffer?.aggregationRules?.forEach((rule) => {
      const hideTsError1 = rule?.serviceRates?.forEach((rate) => {
        if (!serviceRates.map(({ id }) => id).includes(rate.id)) {
          serviceRates.push(toMomentObjectDates(rate));
        }
      });
    });

    const name = offerListSelectMode === "select" ? hierarchyOffer?.name : undefined;

    return {
      id: offerItem.id!,
      parentId,
      key,
      extName: offerItem.extName!,
      name,
      fromDate: offerListSelectMode === "select" ? fromDateSelectMode : initialFromDate,
      quantity: initialQuantity,
      toDate: moment(infinityDateHttpFormat),
      serviceStatusId: undefined,
      offering: {
        ...(hierarchyOffer || {}),
        name,
        agreementId,
        offeringTypeId: offerListSelectMode === "select" ? hierarchyOffer?.offeringTypeId : 3,
        offeringStatusId:
          offerListSelectMode === "select"
            ? hierarchyOffer?.offeringStatusId
            : OFFER_STATUSES.ACTIVE,
        fromDate:
          offerListSelectMode === "select" ? moment(hierarchyOffer?.fromDate) : initialFromDate,
        toDate:
          offerListSelectMode === "select"
            ? moment(hierarchyOffer?.toDate)
            : moment(infinityDateHttpFormat),
        aggregationRules: hierarchyOffer?.aggregationRules?.map((rule) => ({
          ...rule,
          ruleCondition: parseRuleCondition(rule.ruleCondition),
          fromDate: offerListSelectMode === "select" ? moment(rule.fromDate) : initialFromDate,
          toDate:
            offerListSelectMode === "select" ? moment(rule.toDate) : moment(infinityDateHttpFormat),
        })),
        serviceRates: serviceRates.map((rate) => {
          if (offerListSelectMode === "select") {
            return rate;
          }
          return { ...rate, fromDate: initialFromDate, toDate: moment(infinityDateHttpFormat) };
        }),
        serviceProfile: {
          ...hierarchyOffer?.serviceProfile,
          profileTypeId:
            offerListSelectMode === "select" ? hierarchyOffer?.serviceProfile.paymentTypeId : 2,
          fromDate:
            offerListSelectMode === "select"
              ? moment(hierarchyOffer?.serviceProfile.fromDate)
              : initialFromDate,
          toDate:
            offerListSelectMode === "select"
              ? moment(hierarchyOffer?.serviceProfile.toDate)
              : moment(infinityDateHttpFormat),
        },
      },
      minCardinality: offerItem?.minCardinality,
      maxCardinality: offerItem?.maxCardinality,
      defaultCardinality: offerItem?.defaultCardinality,
    };
  };
  // Для генерации  таблицы при изменении offerHierarchy
  const getTableDataForTree = (): IOfferParamsTableRecord[] => {
    const data: IOfferParamsTableRecord[] = [];

    const addOfferHierarchyData = (item: any, parentId?: Key) => {
      data.push(getTableRecordForTree(item, parentId));
      if (item?.children && item.children?.length > 0) {
        item.children.forEach((itemChild) => {
          const itemKeySlice = itemChild.key.split("-").slice(0, -2).join("-"); // для синхронизации с деревом
          addOfferHierarchyData(itemChild, itemKeySlice);
        });
      }
    };

    if (offerHierarchy && treeDataFormat[0]) {
      addOfferHierarchyData(treeDataFormat[0]);
    }

    return data;
  };

  // Для генерации строки таблицы при изменении treeDataFormat для сохранения состояния всех элементов
  const getTableRecordForTreeNotUpdate = (
    offerItem: any,
    parentId?: Key
  ): IOfferParamsTableRecord => {
    const { key } = offerItem;
    const { keyOnRowClicked } = offerItem;
    const hierarchyOffer =
      offerListSelectMode === "new"
        ? offer!
        : hierarchyOfferList.find(({ id }) => id === offerItem.id)!;
    const rootHierarchyOfferId = parentId ? String(parentId).split("-")[0] : offerItem.id;
    const rootHierarchyOffer = rootHierarchyOfferId
      ? hierarchyOfferList.find(({ id }) => id === Number(rootHierarchyOfferId))
      : undefined;

    const findElementInTableData = tableData.find((elTableData) => elTableData.key === key);

    const findElementClickedInTableData = tableData.find(
      (elTableData) => elTableData.key === keyOnRowClicked
    );

    //  При изменении дерева, сохраняем значения всех элементов, если они отображались
    if (findElementInTableData) {
      return {
        ...findElementInTableData,
      };
    }
    const fromDateSelectMode = moment(rootHierarchyOffer?.fromDate).isAfter(moment(), "day")
      ? moment(rootHierarchyOffer?.fromDate)
      : initialFromDate;

    const fromDateNotSelectMode =
      findElementClickedInTableData && !findElementInTableData
        ? moment(findElementClickedInTableData?.fromDate)
        : initialFromDate;

    const fromStatusSelectMode =
      findElementClickedInTableData && !findElementInTableData
        ? findElementClickedInTableData.serviceStatusId
        : undefined;

    const fromStatusNotSelectMode =
      findElementClickedInTableData && !findElementInTableData
        ? findElementClickedInTableData.serviceStatusId
        : undefined;

    const serviceRates =
      hierarchyOffer?.serviceRates?.map((rate) => toMomentObjectDates(rate)) || [];
    const hideTsError = hierarchyOffer?.aggregationRules?.forEach((rule) => {
      const hideTsError1 = rule?.serviceRates?.forEach((rate) => {
        if (!serviceRates.map(({ id }) => id).includes(rate.id)) {
          serviceRates.push(toMomentObjectDates(rate));
        }
      });
    });

    const name = offerListSelectMode === "select" ? hierarchyOffer?.name : undefined;

    return {
      id: offerItem.id!,
      parentId,
      key,
      keyOnRowClicked: undefined,
      extName: offerItem.extName!,
      name,
      fromDate: offerListSelectMode === "select" ? fromDateSelectMode : fromDateNotSelectMode,
      quantity: initialQuantity,
      toDate: moment(infinityDateHttpFormat),
      serviceStatusId:
        offerListSelectMode === "select" ? fromStatusSelectMode : fromStatusNotSelectMode,
      offering: {
        ...(hierarchyOffer || {}),
        name,
        agreementId,
        offeringTypeId: offerListSelectMode === "select" ? hierarchyOffer?.offeringTypeId : 3,
        offeringStatusId: OFFER_STATUSES.ACTIVE,
        fromDate:
          offerListSelectMode === "select"
            ? moment(hierarchyOffer?.fromDate)
            : fromDateNotSelectMode,
        toDate:
          offerListSelectMode === "select"
            ? moment(hierarchyOffer?.toDate)
            : moment(infinityDateHttpFormat),
        aggregationRules: hierarchyOffer?.aggregationRules?.map((rule) => ({
          ...rule,
          ruleCondition: parseRuleCondition(rule.ruleCondition),
          fromDate: offerListSelectMode === "select" ? moment(rule.fromDate) : initialFromDate,
          toDate:
            offerListSelectMode === "select" ? moment(rule.toDate) : moment(infinityDateHttpFormat),
        })),
        serviceRates: serviceRates.map((rate) => {
          if (offerListSelectMode === "select") {
            return rate;
          }
          return { ...rate, fromDate: initialFromDate, toDate: moment(infinityDateHttpFormat) };
        }),
        serviceProfile: {
          ...hierarchyOffer?.serviceProfile,
          profileTypeId:
            offerListSelectMode === "select" ? hierarchyOffer?.serviceProfile.paymentTypeId : 2,
          fromDate:
            offerListSelectMode === "select"
              ? moment(hierarchyOffer?.serviceProfile.fromDate)
              : initialFromDate,
          toDate:
            offerListSelectMode === "select"
              ? moment(hierarchyOffer?.serviceProfile.toDate)
              : moment(infinityDateHttpFormat),
        },
      },
      minCardinality: offerItem?.minCardinality,
      maxCardinality: offerItem?.maxCardinality,
      defaultCardinality: offerItem?.defaultCardinality,
    };
  };
  // Для генерации таблицы при изменении treeDataFormat для сохранения состояния всех элементов
  const getTableDataForTreeNotUpdate = (): IOfferParamsTableRecord[] => {
    const data: IOfferParamsTableRecord[] = [];

    const addOfferHierarchyData = (item: any, parentId?: Key) => {
      data.push(getTableRecordForTreeNotUpdate(item, parentId));
      if (item?.children && item.children?.length > 0) {
        item.children.forEach((itemChild) => {
          const itemKeySlice = itemChild.key.split("-").slice(0, -2).join("-"); // для синхронизации с деревом
          addOfferHierarchyData(itemChild, itemKeySlice);
        });
      }
    };

    if (offerHierarchy && treeDataFormat[0]) {
      addOfferHierarchyData(treeDataFormat[0]);
    }

    return data;
  };

  const [tableData, setTableData] = useState<IOfferParamsTableRecord[]>(
    offerHierarchy ? getTableDataForTree() : getTableData()
  );

  // Проверка что самое первое дерево сгенерированно
  const [isTreeGenerated, setIsTreeGenerated] = useState(false);

  // При изменении дерева перестраиваем таблицу без изменения данных с условием что первый рендер уже прошёл
  useEffect(() => {
    if (offerHierarchy && treeDataFormat[0] && isTreeGenerated) {
      setTableData(getTableDataForTreeNotUpdate());
    }
  }, [treeDataFormat]);

  // При изменении предложения перестраиваем таблицу иерархии заново
  useEffect(() => {
    if (offerHierarchy && treeDataFormat[0]) {
      setTableData(getTableDataForTree());
      setIsTreeGenerated(true);
    }
  }, [offerHierarchy, hierarchyOfferList, offerListSelectMode]);

  const formFields = useMemo((): IFormFieldData[] => {
    const data: IFormFieldData[] = [];
    tableData.forEach(({ key, id, offering, ...recordData }) => {
      Object.entries(recordData).forEach(([fieldName, value]) => {
        data.push({
          name: `${key}-${fieldName}`,
          value,
        });
      });
    });
    return data;
  }, [tableData]);

  // Обновление данных в таблице, по обновлении зависимостей
  useEffect(() => {
    if (!offerHierarchy) {
      setTableData(getTableData());
    }
  }, [offer, hierarchyOfferList, offerListSelectMode]);

  // Очистка ключа от el* символов
  const formatKey = (key) => {
    return key
      ?.split(/-?el*[0-9]*-?/gm)
      .filter(Boolean)
      .join("-");
  };

  // Изменение данных в таблице (кроме вложенных офферов)
  const onChangeTableData = (props: {
    fieldName: keyof IOfferParamsTableRecord;
    key: string;
    value: any;
    changeMode?: "single" | "children" | "all";
  }) => {
    const { key, fieldName, value, changeMode = "single" } = props;

    const parentRecord = tableData.find((rec) => rec.key === key)!;
    const parentServiceModeId = parentRecord.offering.serviceType?.serviceModeId;

    // Условия изменяемости детей
    const getIsChangableChild = (prevRecord: IOfferParamsTableRecord): boolean => {
      switch (fieldName) {
        // Для разовых продуктов меняются вложенные только разовые, для неразовых, только неразовые
        case "serviceStatusId":
          const serviceModeId = prevRecord.offering?.serviceType?.serviceModeId;
          if (parentServiceModeId === 1) {
            return serviceModeId === 1;
          }
          return serviceModeId !== 1;
        default:
          return true;
      }
    };

    setTableData((prev) =>
      prev.map((prevRecord) => {
        switch (changeMode) {
          // single - изменяется только текущая запись
          case "single":
            if (key === prevRecord.key) {
              return { ...prevRecord, [fieldName]: value };
            }
            return prevRecord;
          // children - изменяется текущая запись, а также ее children при isChangableChildrenField
          case "children":
            if (key === prevRecord.key) {
              return { ...prevRecord, [fieldName]: value };
            }
            if (String(prevRecord.key).startsWith(key) && getIsChangableChild(prevRecord)) {
              return { ...prevRecord, [fieldName]: value };
            }
            return prevRecord;
          // all - изменяется текущая запись и все вложения
          case "all":
            return { ...prevRecord, [fieldName]: value };
        }
      })
    );
  };

  // Изменение вложенного в табличную запись оффера
  const onChangeTableOfferData = (props: {
    fieldName: keyof Offer;
    fieldNameTableRow?: keyof IOfferParamsTableRecord;
    key: string;
    value: any;
    changeMode?: "single" | "children" | "all";
  }) => {
    const { fieldName, fieldNameTableRow, key, value, changeMode = "single" } = props;

    const parentRecord = tableData.find((rec) => rec.key === key)!;
    const parentServiceModeId = parentRecord.offering.serviceType?.serviceModeId;

    // Условия изменяемости детей
    const getIsChangableChild = (prevRecord: IOfferParamsTableRecord): boolean => {
      switch (fieldNameTableRow) {
        // Для разовых продуктов меняются вложенные только разовые, для неразовых, только неразовые
        case "serviceStatusId":
          const serviceModeId = prevRecord.offering?.serviceType?.serviceModeId;
          if (parentServiceModeId === 1) {
            return serviceModeId === 1;
          }
          return serviceModeId !== 1;
        default:
          return true;
      }
    };

    const onChangeOffer = (prevRecord) => {
      const getChange = () => {
        // Для профиля, чтобы не затирались значения, которые в форме не содержатся
        const enrichmentValue =
          fieldName === "serviceProfile"
            ? {
                ...prevRecord.offering[fieldName],
                ...value,
              }
            : value;

        // Изменение значения наименования (extName) в дереве
        if (fieldName === "extName") {
          const changeTreeExtName = (tree: DataNode[]): DataNode[] =>
            tree.map((item) => {
              if (item.key === key) {
                return {
                  ...item,
                  title: value,
                };
              }
              return {
                ...item,
                children: item.children?.length ? changeTreeExtName(item.children) : undefined,
              };
            });

          setTreeDataFormat((prev) => changeTreeExtName(prev));
        }

        return {
          ...prevRecord,
          offering: {
            ...prevRecord.offering,
            [fieldName]: enrichmentValue,
          },
        };
      };

      switch (changeMode) {
        // single - изменяется только текущая запись
        case "single":
          if (prevRecord.key === key) {
            return getChange();
          }
          return prevRecord;
        // children - изменяется текущая запись, а также ее children при isChangableChildrenField
        case "children":
          if (prevRecord.key === key) {
            return getChange();
          }
          if (String(prevRecord.key).startsWith(key) && getIsChangableChild(prevRecord)) {
            return getChange();
          }
          return prevRecord;
        // all - изменяется текущая запись и все вложения
        case "all":
          return getChange();
      }
    };

    setTableData((prev) =>
      prev.map((prevRecord) => {
        return onChangeOffer(prevRecord);
      })
    );
  };

  // console.log({ tableData });

  return {
    tableData,
    formFields,
    onChangeTableData,
    onChangeTableOfferData,
  };
};
